/* ============================================================
   THE PRIVÉ ARCHIVE — theme.js
   Shopify AJAX Cart API + UI interactions
   ============================================================ */

'use strict';

/* ---- Utility ------------------------------------------------ */
function debounce(fn, ms) {
  let timer;
  return function (...args) {
    clearTimeout(timer);
    timer = setTimeout(() => fn.apply(this, args), ms);
  };
}

function formatMoney(cents) {
  return '$' + (cents / 100).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
}

/* ---- Toast Notifications ------------------------------------ */
function showToast(msg, duration = 3000) {
  const container = document.getElementById('toast-container');
  if (!container) return;
  const toast = document.createElement('div');
  toast.className = 'toast';
  toast.textContent = msg;
  container.appendChild(toast);
  requestAnimationFrame(() => {
    requestAnimationFrame(() => toast.classList.add('toast-visible'));
  });
  setTimeout(() => {
    toast.classList.add('out');
    toast.addEventListener('animationend', () => toast.remove(), { once: true });
    setTimeout(() => toast.remove(), 500);
  }, duration);
}

/* ---- Shopify Cart API --------------------------------------- */
const ShopifyCart = {
  async get() {
    const res = await fetch('/cart.js', { headers: { 'Content-Type': 'application/json' } });
    if (!res.ok) throw new Error('Cart fetch failed');
    return res.json();
  },
  async add(variantId, quantity = 1) {
    const res = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items: [{ id: variantId, quantity }] }),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.description || 'Could not add to cart');
    }
    return res.json();
  },
  async change(line, quantity) {
    const res = await fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ line, quantity }),
    });
    if (!res.ok) throw new Error('Cart update failed');
    return res.json();
  },
  async remove(line) {
    return this.change(line, 0);
  },
};

/* ---- Cart Drawer -------------------------------------------- */
const CartDrawer = {
  el: null,
  overlay: null,
  itemsEl: null,
  footerEl: null,
  countEl: null,
  totalEl: null,

  init() {
    this.el = document.getElementById('cart-drawer');
    this.overlay = document.getElementById('cart-overlay');
    this.itemsEl = document.getElementById('cart-items');
    this.footerEl = document.getElementById('cart-footer');
    this.countEl = document.getElementById('cart-count');
    this.totalEl = document.getElementById('cart-total');

    document.getElementById('cart-open')?.addEventListener('click', () => this.open());
    document.getElementById('cart-close')?.addEventListener('click', () => this.close());
    this.overlay?.addEventListener('click', () => this.close());

    // Initialise cart count
    ShopifyCart.get().then(cart => this.updateCount(cart.item_count)).catch(() => {});
  },

  open() {
    this.el?.classList.add('open');
    this.overlay?.classList.add('open');
    this.overlay?.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';
    this.render();
  },

  close() {
    this.el?.classList.remove('open');
    this.overlay?.classList.remove('open');
    this.overlay?.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
  },

  async render() {
    if (!this.itemsEl) return;
    this.itemsEl.innerHTML = '<div style="padding:32px;text-align:center;color:var(--muted);font-size:13px;">Loading…</div>';

    try {
      const cart = await ShopifyCart.get();
      this.updateCount(cart.item_count);

      if (cart.item_count === 0) {
        this.itemsEl.innerHTML = `
          <div class="cart-empty">
            <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1"><path d="M6 2 3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z"/><line x1="3" y1="6" x2="21" y2="6"/><path d="M16 10a4 4 0 0 1-8 0"/></svg>
            <p>Your cart is empty.</p>
            <a href="/collections/all" class="btn btn-dark" style="margin-top:12px;">Shop Now</a>
          </div>`;
        this.footerEl && (this.footerEl.style.display = 'none');
        return;
      }

      this.footerEl && (this.footerEl.style.display = '');
      if (this.totalEl) this.totalEl.textContent = formatMoney(cart.total_price);

      this.itemsEl.innerHTML = cart.items.map((item, idx) => `
        <div class="cart-item" data-line="${idx + 1}">
          <a href="${item.url}" class="cart-item-thumb" style="flex-shrink:0;">
            ${item.image ? `<img src="${item.image}" alt="${item.product_title}" style="width:88px;height:110px;object-fit:contain;display:block;">` : ''}
          </a>
          <div class="cart-item-info">
            <a href="${item.url}" class="cart-item-name">${item.product_title}</a>
            ${item.variant_title && item.variant_title !== 'Default Title' ? `<p class="cart-item-variant">${item.variant_title}</p>` : ''}
            <p class="cart-item-price">${formatMoney(item.final_line_price)}</p>
            <div class="cart-item-actions">
              <button class="qty-btn qty-down" data-line="${idx + 1}" data-qty="${item.quantity - 1}" aria-label="Decrease">−</button>
              <span class="qty-num">${item.quantity}</span>
              <button class="qty-btn qty-up" data-line="${idx + 1}" data-qty="${item.quantity + 1}" aria-label="Increase">+</button>
              <button class="cart-remove" data-line="${idx + 1}" aria-label="Remove">Remove</button>
            </div>
          </div>
        </div>
      `).join('');

      // Qty + remove listeners
      this.itemsEl.querySelectorAll('.qty-btn').forEach(btn => {
        btn.addEventListener('click', async () => {
          const line = parseInt(btn.dataset.line);
          const qty = Math.max(0, parseInt(btn.dataset.qty));
          btn.disabled = true;
          try {
            await ShopifyCart.change(line, qty);
            this.render();
          } catch (e) {
            showToast('Could not update cart.');
            btn.disabled = false;
          }
        });
      });

      this.itemsEl.querySelectorAll('.cart-remove').forEach(btn => {
        btn.addEventListener('click', async () => {
          const line = parseInt(btn.dataset.line);
          btn.textContent = '…';
          btn.disabled = true;
          try {
            await ShopifyCart.remove(line);
            this.render();
          } catch (e) {
            showToast('Could not remove item.');
          }
        });
      });

    } catch (e) {
      this.itemsEl.innerHTML = '<div style="padding:32px;text-align:center;color:var(--muted);font-size:13px;">Could not load cart.</div>';
    }
  },

  updateCount(count) {
    if (!this.countEl) return;
    this.countEl.textContent = count;
    this.countEl.style.display = count > 0 ? '' : 'none';
  },
};

// Expose globally so product.liquid can use it
window.TPACartDrawer = CartDrawer;

/* ---- Add to Cart (product card + quick-add buttons) --------- */
document.addEventListener('click', async e => {
  const btn = e.target.closest('.product-card-add');
  if (!btn) return;
  const variantId = parseInt(btn.dataset.variantId);
  if (!variantId) return;
  btn.disabled = true;
  const original = btn.innerHTML;
  btn.innerHTML = '…';
  try {
    await ShopifyCart.add(variantId);
    const cart = await ShopifyCart.get();
    CartDrawer.updateCount(cart.item_count);
    showToast('Added to cart.');
    CartDrawer.open();
  } catch (e) {
    showToast(e.message || 'Could not add to cart.');
  } finally {
    btn.disabled = false;
    btn.innerHTML = original;
  }
});

/* ---- Quick View Modal --------------------------------------- */
const Modal = {
  el: null,
  overlay: null,

  init() {
    this.el = document.getElementById('product-modal');
    this.overlay = document.getElementById('modal-overlay');
    document.getElementById('modal-close')?.addEventListener('click', () => this.close());
    this.overlay?.addEventListener('click', () => this.close());

    document.getElementById('modal-add-cart')?.addEventListener('click', async function () {
      const variantId = parseInt(this.dataset.variantId);
      if (!variantId) return;
      this.disabled = true;
      this.textContent = 'Adding…';
      try {
        await ShopifyCart.add(variantId);
        const cart = await ShopifyCart.get();
        CartDrawer.updateCount(cart.item_count);
        showToast('Added to cart.');
        Modal.close();
        CartDrawer.open();
      } catch (e) {
        showToast(e.message || 'Could not add to cart.');
      } finally {
        this.disabled = false;
        this.textContent = 'Add to Cart';
      }
    });
  },

  open(card) {
    if (!this.el) return;

    const title       = card.dataset.productTitle || '';
    const price       = card.dataset.productPrice || '';
    const category    = card.dataset.productCategory || '';
    const desc        = card.dataset.productDesc || '';
    const productUrl  = card.dataset.productUrl || '#';
    const productHandle = card.dataset.productHandle || (productUrl.split('/products/')[1] || '').split('?')[0];

    let images = [];
    try { images = JSON.parse(card.dataset.productImages || '[]'); } catch(e) {}
    if (!images.length && card.dataset.productImage) images = [card.dataset.productImage];
    this._images = images;
    this._imgIndex = 0;

    document.getElementById('modal-name').textContent  = title;
    document.getElementById('modal-price').textContent = price;
    document.getElementById('modal-desc').textContent  = desc;
    const link = document.getElementById('modal-product-link');
    if (link) link.href = productUrl;

    // Clear variants & reset button
    const variantsEl = document.getElementById('modal-variants');
    if (variantsEl) variantsEl.innerHTML = '';
    const addBtn = document.getElementById('modal-add-cart');
    addBtn.dataset.variantId = card.dataset.variantId || '';
    addBtn.textContent = 'Add to Cart';
    addBtn.disabled = false;

    this._showImage();

    const prev = document.getElementById('modal-prev');
    const next = document.getElementById('modal-next');
    if (prev) prev.style.display = images.length > 1 ? 'flex' : 'none';
    if (next) next.style.display = images.length > 1 ? 'flex' : 'none';

    this.el.classList.add('open');
    this.overlay?.classList.add('open');
    this.overlay?.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';

    // Fetch variants
    if (productHandle) {
      fetch('/products/' + productHandle + '.js')
        .then(r => r.json())
        .then(product => {
          if (!product.variants || product.variants.length <= 1) return;
          // Group by option
          const options = product.options || [];
          if (!variantsEl) return;
          variantsEl.innerHTML = options.map((opt, oi) => {
            const optName = typeof opt === 'object' ? (opt.name || opt) : opt;
            const values = [...new Set(product.variants.map(v => v.options[oi]))];
            return `<div class="modal-option-group">
              <p class="modal-option-label">${optName}</p>
              <div class="modal-option-btns">
                ${values.map((val, vi) => `<button class="modal-opt-btn${vi === 0 ? ' selected' : ''}" data-option-index="${oi}" data-value="${val}">${val}</button>`).join('')}
              </div>
            </div>`;
          }).join('');

          // Store variants on modal
          this._variants = product.variants;
          this._selectedOptions = product.variants[0].options.slice();

          // Set initial variant
          this._updateVariant();

          // Option click handlers
          variantsEl.querySelectorAll('.modal-opt-btn').forEach(btn => {
            btn.addEventListener('click', () => {
              const oi = parseInt(btn.dataset.optionIndex);
              this._selectedOptions[oi] = btn.dataset.value;
              btn.closest('.modal-option-btns').querySelectorAll('.modal-opt-btn').forEach(b => b.classList.remove('selected'));
              btn.classList.add('selected');
              this._updateVariant();
            });
          });
        })
        .catch(() => {});
    }
  },

  _updateVariant() {
    if (!this._variants) return;
    const match = this._variants.find(v => v.options.every((o, i) => o === this._selectedOptions[i]));
    const addBtn = document.getElementById('modal-add-cart');
    const priceEl = document.getElementById('modal-price');
    if (!match) return;
    addBtn.dataset.variantId = match.id;
    addBtn.disabled = !match.available;
    addBtn.textContent = match.available ? 'Add to Cart' : 'Sold Out';
    if (priceEl) {
      const p = parseFloat(match.price);
      priceEl.textContent = isNaN(p) ? '' : (p > 1000 ? '$' + (p / 100).toFixed(2) : '$' + p.toFixed(2));
    }
    // Update image to variant's featured image if available
    if (match.featured_image && match.featured_image.src) {
      const src = match.featured_image.src;
      // Try to find this image in the _images array by matching base URL
      const baseUrl = src.split('?')[0].replace(/(_\d+x\d*|_\d*x\d+)(\.[^.]+)$/, '$2');
      const idx = this._images.findIndex(img => {
        const b = img.split('?')[0].replace(/(_\d+x\d*|_\d*x\d+)(\.[^.]+)$/, '$2');
        return b === baseUrl;
      });
      if (idx !== -1) {
        this._imgIndex = idx;
        this._showImage();
      } else {
        // Image not in array — show it directly resized
        const resized = src.replace(/(\.[^.?]+)(\?|$)/, '_800x$1$2');
        const el = document.getElementById('modal-image');
        if (el) {
          el.style.backgroundImage = `url('${resized}')`;
          el.style.backgroundSize = 'contain';
          el.style.backgroundPosition = 'center';
          el.style.backgroundRepeat = 'no-repeat';
        }
      }
    }
  },

  _showImage() {
    const img = this._images[this._imgIndex] || '';
    const el = document.getElementById('modal-image');
    if (el) {
      el.style.backgroundImage = img ? `url('${img}')` : '';
      el.style.backgroundSize = 'contain';
      el.style.backgroundPosition = 'center';
      el.style.backgroundRepeat = 'no-repeat';
    }
  },

  prevImage() {
    if (!this._images.length) return;
    this._imgIndex = (this._imgIndex - 1 + this._images.length) % this._images.length;
    this._showImage();
  },

  nextImage() {
    if (!this._images.length) return;
    this._imgIndex = (this._imgIndex + 1) % this._images.length;
    this._showImage();
  },

  close() {
    this.el?.classList.remove('open');
    this.overlay?.classList.remove('open');
    this.overlay?.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
  },
};

document.addEventListener('click', e => {
  const btn = e.target.closest('.quick-add');
  if (!btn) return;
  e.preventDefault();
  const card = btn.closest('.product-card');
  if (card) Modal.open(card);
});

document.getElementById('modal-prev')?.addEventListener('click', () => Modal.prevImage());
document.getElementById('modal-next')?.addEventListener('click', () => Modal.nextImage());

/* ---- Search Overlay ---------------------------------------- */
const Search = {
  overlay: null,
  input: null,
  results: null,

  init() {
    this.overlay = document.getElementById('search-overlay');
    this.input   = document.getElementById('search-input');
    this.results = document.getElementById('search-results');

    document.querySelectorAll('.search-open-btn').forEach(btn => btn.addEventListener('click', () => this.open()));
    document.getElementById('search-close')?.addEventListener('click', () => this.close());

    document.addEventListener('keydown', e => {
      if (e.key === 'Escape') {
        if (this.overlay?.classList.contains('open')) this.close();
        if (document.getElementById('product-modal')?.classList.contains('open')) Modal.close();
      }
    });

    this.input?.addEventListener('input', debounce(() => this.query(this.input.value.trim()), 280));
  },

  open() {
    this.overlay?.classList.add('open');
    this.input?.focus();
  },

  close() {
    this.overlay?.classList.remove('open');
    if (this.input) this.input.value = '';
    if (this.results) this.results.innerHTML = '';
  },

  async query(q) {
    if (!this.results) return;
    if (q.length < 2) { this.results.innerHTML = ''; return; }

    try {
      const url = `/search/suggest.json?q=${encodeURIComponent(q)}&resources[type]=product&resources[limit]=6`;
      const res = await fetch(url);
      const data = await res.json();
      const products = data.resources?.results?.products || [];

      if (products.length === 0) {
        this.results.innerHTML = '<p class="search-empty">No results found.</p>';
        return;
      }

      this.results.innerHTML = products.map(p => `
        <a href="${p.url}" class="search-result-item">
          ${p.image ? `<img class="search-result-thumb" src="${p.image}" alt="${p.title}" width="48" height="48">` : ''}
          <div class="search-result-info">
            <p class="search-result-name">${p.title}</p>
            <p class="search-result-price">${p.price}</p>
          </div>
        </a>
      `).join('') + `<a href="/search?q=${encodeURIComponent(q)}" class="search-view-all">View all results →</a>`;
    } catch (e) {
      this.results.innerHTML = '';
    }
  },
};

/* ---- Hamburger / Mobile Nav -------------------------------- */
function initHamburger() {
  const hamburger = document.getElementById('hamburger');
  const mobileNav = document.getElementById('mobile-nav');
  if (!hamburger || !mobileNav) return;

  hamburger.addEventListener('click', () => {
    const isOpen = mobileNav.classList.toggle('open');
    hamburger.setAttribute('aria-expanded', isOpen);
    hamburger.classList.toggle('open', isOpen);
  });

  // Close mobile nav when a link is clicked
  mobileNav.querySelectorAll('a').forEach(link => {
    link.addEventListener('click', () => {
      mobileNav.classList.remove('open');
      hamburger.classList.remove('open');
      hamburger.setAttribute('aria-expanded', 'false');
    });
  });
}

/* ---- Scroll-aware Header ----------------------------------- */
function initScrollHeader() {
  const header = document.getElementById('site-header');
  if (!header) return;
  let lastScroll = 0;

  window.addEventListener('scroll', () => {
    const scroll = window.scrollY;
    header.classList.toggle('scrolled', scroll > 60);
    header.classList.toggle('hidden', scroll > lastScroll && scroll > 300);
    lastScroll = scroll;
  }, { passive: true });
}

/* ---- Newsletter (client-side feedback only) ---------------- */
function initNewsletter() {
  // Shopify's {% form 'customer' %} handles submission natively.
  // This just provides a smooth UX for the inline form version
  // if it's not using the Shopify form tag (e.g. custom AJAX).
}

/* ---- Init -------------------------------------------------- */
document.addEventListener('DOMContentLoaded', () => {
  CartDrawer.init();
  Modal.init();
  Search.init();
  initHamburger();
  initScrollHeader();
  initNewsletter();
});
